import _ from 'lodash';
import React, {FC} from 'react';
import {connect} from 'react-redux';

import {ResolveDispatchProps} from '../../../../../../../../../src/helpers/reduxHelpers';
import {fetchCustomFieldsForResourceTypeAsyncAction} from '../../../../../../../../../src/store/data/customFields/customFieldsActions';
import {getCustomFields} from '../../../../../../../../../src/store/data/dataSelectors';
import {getIsUserGlobalAdmin} from '../../../../../../../../../src/store/data/teammates/teammatesSelectors';
import {
  CrmPreviewMapping,
  popoutCrmPreviewAction,
} from '../../../../../../../../../src/store/panels/contacts/contactsManager/crmPreview/crmPreviewActions';
import {
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../../../../../../srcCommon/helpers/reselectHelpers';
import {useInitialEffect} from '../../../../../../../../shared/helpers/src/react/hooks/useEffectHelpers';
import {CrmSyncOptionsEnum, EXTERNAL_LINK_SYNC_ALL} from '../crmSyncsOptions/crmSyncsOptions';
import {computeCustomFieldResourceType} from '../crmSyncsPageConfig/crmSyncsPageConfigHelpers';
import {CrmSyncsMapping, CrmSyncsMappingProps} from './crmSyncsMapping';

/*
 * Props.
 */

interface CrmSyncsMappingContainerProps
  extends Omit<CrmSyncsMappingProps, 'customFields' | 'isAdmin' | 'onPreviewClick'> {}

/*
 * Components.
 */

export const CrmMappingContainerComponent: FC<
  CrmSyncsMappingContainerProps &
    ResolveStateProps<typeof mapStateToProps> &
    ResolveDispatchProps<typeof mapDispatchToProps>
> = (props) => {
  const {
    panelId,
    crmSyncId,
    selectedOption,
    onPreviewClick,
    source,
    resourceType,
    customFields,
    isDisabled,
    mappedFields,
    isLoading,
    isAdmin,
    crmFields,
    crmMetadata,
    setMappedFields,
    fetchCustomFieldsForResourceType,
  } = props;

  const customFieldResourceType = computeCustomFieldResourceType(resourceType);

  // Maybe fetch custom fields if they are not loaded yet, this could happen if they go directly to
  // settings without opening contacts manager or P4 contact details.
  useInitialEffect(() => {
    if (
      _(customFields)
        .values()
        .some((customField) => customField?.resourceType === customFieldResourceType)
    ) {
      return;
    }
    fetchCustomFieldsForResourceType(customFieldResourceType);
  });

  return (
    <CrmSyncsMapping
      panelId={panelId}
      crmSyncId={crmSyncId}
      selectedOption={selectedOption}
      onPreviewClick={onPreviewClick}
      source={source}
      setMappedFields={setMappedFields}
      resourceType={resourceType}
      customFields={customFields}
      crmFields={crmFields}
      crmMetadata={crmMetadata}
      isDisabled={isDisabled}
      mappedFields={mappedFields}
      isLoading={isLoading}
      isAdmin={isAdmin}
    />
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps()(() => ({
  customFields: getCustomFields,
  isAdmin: getIsUserGlobalAdmin,
}));

const mapDispatchToProps = makeMapDispatchToProps<CrmSyncsMappingContainerProps>()(
  ({panelId, crmSyncId, selectedOption, source, resourceType, crmFilter}) => ({
    fetchCustomFieldsForResourceType: fetchCustomFieldsForResourceTypeAsyncAction,
    onPreviewClick: (mapping: CrmPreviewMapping) => {
      if (!crmSyncId || !selectedOption) {
        return undefined;
      }
      const listId =
        selectedOption.type === CrmSyncOptionsEnum.ALL ? EXTERNAL_LINK_SYNC_ALL : selectedOption.listId;
      return popoutCrmPreviewAction(panelId, crmSyncId, source, resourceType, mapping, listId, crmFilter);
    },
  }),
);

export const CrmSyncsMappingContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(CrmMappingContainerComponent);
